import React, { useState, useRef, useEffect } from "react";
import Pagination from "../Pagination";
import "./index.scss";
import { usePermissions } from "../../utils/roleUtils";

// ── 3-dot action menu ─────────────────────────────────────────────────────────
const ActionMenu = ({ row, onView, onEdit, onResend }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { canResend } = usePermissions();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="action-menu" ref={ref}>
      <button
        className="action-menu__trigger"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((p) => !p);
        }}
        title="Actions"
      >
        ⋮
      </button>
      {open && (
        <div className="action-menu__dropdown">
          <button
            className="action-menu__item"
            onClick={() => {
              setOpen(false);
              onView(row);
            }}
          >
            View
          </button>
          <button
            className="action-menu__item"
            onClick={() => {
              setOpen(false);
              onEdit(row);
            }}
          >
            Edit
          </button>
          {canResend && onResend && (
            <button
              className="action-menu__item"
              onClick={() => {
                setOpen(false);
                onResend(row);
              }}
            >
              Resend Link
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Table ─────────────────────────────────────────────────────────────────────
const Table = ({
  columns,
  data,
  onEdit,
  onDelete,
  onResend,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  showPagination = false,
  showPaginationInfo = true,
  expandedRow,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (index) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th key={i}>{col.header}</th>
              ))}
              {onEdit && <th className="th-actions">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <tr
                    onClick={
                      expandedRow ? () => toggleRow(rowIndex) : undefined
                    }
                    style={expandedRow ? { cursor: "pointer" } : undefined}
                  >
                    {columns.map((col, colIndex) => (
                      <td key={colIndex}>
                        {col.render ? col.render(row) : row[col.accessor]}
                      </td>
                    ))}
                    {onEdit && (
                      <td
                        className="td-actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu
                          row={row}
                          onView={(r) => onEdit(r, true)}
                          onEdit={(r) => onEdit(r, false)}
                          onResend={onResend}
                        />
                      </td>
                    )}
                  </tr>
                  {expandedRow && expandedRows.has(rowIndex) && (
                    <tr className="expanded-row">
                      <td colSpan={columns.length + (onEdit ? 1 : 0)}>
                        {expandedRow(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length + (onEdit ? 1 : 0)}
                  className="no-data"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="table-pagination">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            showInfo={showPaginationInfo}
          />
        </div>
      )}
    </div>
  );
};

export default Table;
